<script lang="ts" setup>
import type { TripRoute, TripRouteFreight, TripRouteFreightWithOrder, TripRouteStop, TripStop } from "@/types/trip";
import { computed } from "vue";
import { useFreightOrdinals, useFreights, useOrder } from "@/composables";
import AlertMessage from "@/components/messages/AlertMessage.vue";
import RouteStopFreight from "@/views/trips/edit/components/RouteStopFreight.vue";
import { OrderFreightState } from "@/constants/order";
import DeletedChip from "@/components/chips/DeletedChip.vue";
import { getEntityKey, isSameEntity } from "@/utils/sameEntity";
import type { OrderFreightDto } from "@/api/orders/dto/order.dto";

const { isReloadStop, generateId } = useOrder();

const stopFreights = defineModel<TripRouteFreight[]>({ default: [] });

const emits = defineEmits<{
  (e: "update:modelValue", freights: TripRouteFreight[]): void;
}>();

const props = defineProps<{
  stop: TripRouteStop;
  tripStops: TripStop[];
  routes: TripRoute[];
  freightsFromApi: TripRouteFreight[];
  route: TripRoute;
  orderFreights: OrderFreightDto[];
}>();

const { getFreightName, shouldUpdateRouteFreight } = useFreights();

const routeStops = computed(() => {
  return props.routes.flatMap((route) => route.routeStops);
});
const { getFreightOrdinalLabel } = useFreightOrdinals(() => routeStops.value);

const relatedReloadStop = computed<TripStop | undefined>(() => {
  const tripStop = props.tripStops.find((tripStop) => tripStop.id === props.stop.tripStopId);

  if (!tripStop) {
    return undefined;
  }

  return isReloadStop(tripStop) ? tripStop : undefined;
});

function createFreightName(freight: TripRouteFreight | OrderFreightDto): string {
  const label = getFreightOrdinalLabel(getEntityKey(freight));

  return getFreightName(freight, label, "", false, true);
}

function createFreightNumber(freight: TripRouteFreight | OrderFreightDto): number {
  const label = getFreightOrdinalLabel(getEntityKey(freight));

  return Number(label?.split("-")[1]) || 0;
}

const reloadFreights = computed<TripRouteFreightWithOrder[]>(() => {
  if (relatedReloadStop.value && isReloadStop(relatedReloadStop.value)) {
    const reloadFreights = relatedReloadStop.value?.reloadFreights || [];

    const result = reloadFreights
      .map((freight) => {
        const name = createFreightName(freight);
        const number = createFreightNumber(freight);

        const truck =
          props.routes.find((route) => route.freights.some((routeFreight) => isSameEntity(routeFreight, freight)))
            ?.truck || null;

        const order = props.tripStops.find((tripStop) => tripStop.freights?.includes(freight.freightId))?.order;

        return {
          ...freight,
          truck: truck,
          order,
          name,
          number,
        };
      })
      .sort((a, b) => a.number - b.number);

    return result;
  }

  return [];
});

const deletedReloadFreights = computed(() => {
  return stopFreights.value.filter(
    (freight) =>
      !relatedReloadStop.value?.reloadFreights?.find(
        (reloadFreight) => getEntityKey(reloadFreight) === freight.parentFreightId,
      ),
  );
});

function getFreightPoints(freight: TripRouteFreight) {
  const relatedOrderStops = props.tripStops.filter((tripStop) => tripStop?.freights?.includes(freight.freightId));

  return relatedOrderStops.map((stop) => {
    return {
      ...stop.facility,
      fullAddress: stop.facility?.address,
    };
  });
}

function hasStopFreightForReloadFreight(reloadFreight: TripRouteFreight): boolean {
  return stopFreights.value.some((stopFreight) => stopFreight.parentFreightId === getEntityKey(reloadFreight));
}

function addFreight(reloadFreight: TripRouteFreight) {
  const hasRelatedFreight = hasStopFreightForReloadFreight(reloadFreight);
  const newFreight: TripRouteFreight = {
    ...reloadFreight,
    id: generateId(),
    $existedUIOnly: true,
    parentFreightId: getEntityKey(reloadFreight),
    plannedQuantity: hasRelatedFreight ? 1 : reloadFreight.plannedQuantity,
    plannedWeight: hasRelatedFreight ? 1 : reloadFreight.plannedWeight,
    number: stopFreights.value.length + 1,
  };

  const updatedFreights = [...stopFreights.value, newFreight];
  emits("update:modelValue", updatedFreights);
}

function getStopFreightsByReloadFreight(reloadFreight: TripRouteFreight) {
  return stopFreights.value.filter((stopFreight) => stopFreight.parentFreightId === getEntityKey(reloadFreight));
}

function getReloadFreightClass(reloadFreight: TripRouteFreight) {
  if (!reloadFreight) {
    return;
  }

  const parentState = reloadFreight.state;

  if (reloadFreight.isDeleted) {
    return "freight--deleted";
  }

  if (parentState === OrderFreightState.new) {
    return "freight--new";
  }

  if (shouldUpdateRouteFreight(reloadFreight)) {
    return "freight--updated";
  }

  return "";
}
</script>

<template>
  <div>
    <div class="text-grey-darken-3 font-size-16 font-weight-500">Route freights:</div>

    <div v-if="deletedReloadFreights.length" class="pa-4 bg-grey-lighten-4 mt-4">
      <div class="text-grey-darken-3 font-size-16 font-weight-500">Route freights has been deleted</div>

      <div class="text-grey mt-6">Add route freight to be picked up at reload stop</div>

      <route-stop-freight
        v-for="freight of deletedReloadFreights"
        :key="getEntityKey(freight)"
        :freights-from-api="freightsFromApi"
        :order-freights="orderFreights"
        :route="route"
        :route-freight="freight"
        :route-stop="stop"
      />
    </div>

    <div class="freights">
      <template v-if="reloadFreights.length">
        <div
          v-for="(reloadFreight, index) of reloadFreights"
          :key="`${getEntityKey(reloadFreight)}-${index}`"
          :class="getReloadFreightClass(reloadFreight)"
          class="mt-4 freight"
        >
          <div class="freight__header pa-4 bg-grey-lighten-4">
            <div class="d-flex align-center justify-space-between">
              <div>
                <span class="font-size-16 text-grey-darken-3 font-weight-500 freight__name">{{
                  reloadFreight.name
                }}</span>

                <router-link
                  v-if="reloadFreight.order"
                  :to="{ name: 'order-view', params: { id: reloadFreight.order.id } }"
                  class="text-primary text-decoration-none ml-4 font-weight-500 freight__link"
                  target="_blank"
                >
                  Order#{{ reloadFreight.order?.number }}
                </router-link>

                <router-link
                  v-if="reloadFreight.truck?.id"
                  :to="{ name: 'truck-view', params: { id: reloadFreight.truck.id } }"
                  class="text-primary text-decoration-none ml-4 font-weight-500 freight__link"
                  target="_blank"
                >
                  <v-icon size="small">mdi-truck-outline</v-icon>
                  Truck#{{ reloadFreight.truck.number }}
                </router-link>
              </div>

              <deleted-chip v-if="reloadFreight.isDeleted" />
            </div>

            <div class="text-grey-darken-1 mt-1">
              <div
                v-for="(point, i) in getFreightPoints(reloadFreight)"
                :key="point.id"
                class="freight__point d-inline-block"
              >
                {{ point.fullAddress }}
                <v-icon
                  v-if="i < getFreightPoints(reloadFreight).length - 1"
                  class="mx-1"
                  icon="mdi-arrow-right"
                  size="14"
                ></v-icon>
              </div>
            </div>

            <alert-message
              v-if="reloadFreight.isDeleted"
              bg-color="indigo-lighten-5"
              border-color="none"
              class="mt-4"
              color="indigo"
              icon-color="indigo"
              >Freight#{{ reloadFreight.number }} will be removed after re-dispatch confirmation
            </alert-message>
          </div>

          <div class="pa-4 bg-grey-lighten-5 freight__body">
            <alert-message v-if="shouldUpdateRouteFreight(reloadFreight)" class="mb-4"
              >Please update route freight information
            </alert-message>

            <div class="text-grey-darken-2">Add route freight to be picked up at reload stop</div>

            <template
              v-for="routeFreight in getStopFreightsByReloadFreight(reloadFreight)"
              :key="getEntityKey(routeFreight)"
            >
              <route-stop-freight
                v-model="stopFreights"
                :freights-from-api="freightsFromApi"
                :order-freights="orderFreights"
                :related-reload-freight="reloadFreight"
                :route="route"
                :route-freight="routeFreight"
                :route-stop="stop"
              />
            </template>

            <v-btn
              :disabled="reloadFreight.isDeleted"
              class="mt-4"
              data-qa="add-route-freight-button"
              density="compact"
              variant="text"
              @click="addFreight(reloadFreight)"
            >
              <div class="d-flex align-center text-primary text-uppercase font-size-11 font-weight-600">
                <v-icon class="mr-2" icon="mdi-plus"></v-icon>
                Add route freight
              </div>
            </v-btn>
          </div>
        </div>
      </template>

      <div v-else class="d-flex align-center justify-center mt-4 bg-grey-lighten-4 pa-4">
        <span class="font-size-16 font-weight-500 text-grey-darken-1">
          Freight is not added to any of the Delivery reload stops
        </span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.freight {
  &__body {
    border-radius: 0 0 8px 8px;
    border-right: 1px solid $grey-lighten-2;
    border-bottom: 1px solid $grey-lighten-2;
    border-left: 1px solid $grey-lighten-2;
  }

  &--updated {
    border: 1px solid $warning;

    .freight__header {
      background-color: $warning-light !important;
    }
  }

  &--not-added {
    border: 1px solid $success;

    .freight__header {
      background-color: $green-lighten-5 !important;
    }
  }

  &--deleted {
    .freight__header {
      background-color: $grey-lighten-4 !important;
    }

    .freight__link {
      color: $grey !important;
    }

    .freight__name {
      color: $grey !important;
    }

    .freight__point {
      color: $grey !important;
    }
  }

  &--new {
    border: 1px solid $green;

    .freight__header {
      background-color: $green-lighten !important;
    }
  }
}
</style>
